/**
 * Shared search input + sort/filter button row for mobile scouting toolbars.
 *
 * Renders a search input with clear button, a Sort button, and a Filters
 * button with an active filter count badge. Used by both MobileTargetsToolbar
 * and MobileScoutReportsToolbar to eliminate duplication.
 * @module features/scouting/components/MobileSearchSortFilterBar/MobileSearchSortFilterBarView
 */

import React, { memo } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  SlidersHorizontal,
  Search,
  X,
} from "lucide-react-native";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import ActiveFilterCountBadge from "@/components/organisms/shared/ActiveFilterCountBadge";

/** Props for MobileSearchSortFilterBarView */
export interface MobileSearchSortFilterBarViewProps {
  /** Current search input text */
  readonly searchText: string;
  /** Callback when search text changes */
  readonly onSearchChange: (text: string) => void;
  /** Callback to clear the search input */
  readonly onClearSearch: () => void;
  /** Callback when the search input gains focus */
  readonly onInputFocus?: () => void;
  /** Callback when the search input loses focus */
  readonly onInputBlur?: () => void;
  /** Whether to show the Sort and Filter buttons */
  readonly showSortFilter?: boolean;
  /** Placeholder text for the search input */
  readonly searchPlaceholder: string;
  /** Callback when the Sort button is pressed */
  readonly onSortPress: () => void;
  /** Callback when the Filters button is pressed */
  readonly onFiltersPress: () => void;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Number of active filters for the badge */
  readonly activeFilterCount: number;
  /** Active sort field label — when set, button shows active state */
  readonly activeSortLabel?: string;
  /** Active sort direction — controls arrow icon direction */
  readonly activeSortDir?: "asc" | "desc";
  /** Optional testID for the Sort button (e2e) */
  readonly sortTestID?: string;
  /** Optional testID for the Filters button (e2e) */
  readonly filterTestID?: string;
}

/**
 * Resolves the sort button icon based on active sort state
 * @param label - Active sort field label, undefined when no sort active
 * @param dir - Sort direction, undefined when no sort active
 */
const getSortIcon = (
  label: string | undefined,
  dir: "asc" | "desc" | undefined
) => (label ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown);

/** Active-state chip border/background shared by the Sort and Filters buttons */
const ACTIVE_CHIP_CLASS = "border-2 border-accent-primary bg-accent-primary/15";
/** Inactive-state chip border/background shared by both buttons */
const INACTIVE_CHIP_CLASS =
  "border border-outline-default bg-surface-base dark:border-outline-strong dark:bg-surface-raised";

/**
 * Builds a Sort/Filters chip className with active-state border + background.
 * @param active - Whether the chip is in its active (selection present) state
 */
const chipClass = (active: boolean): string =>
  `flex-row items-center gap-1 rounded-sm px-3 py-2 ${
    active ? ACTIVE_CHIP_CLASS : INACTIVE_CHIP_CLASS
  }`;

/**
 * Sort button label — prefixed with "Sort: " when a sort is active (AC#5).
 * @param label - Active sort field label, undefined when no sort active
 */
const getSortButtonLabel = (label: string | undefined): string =>
  label ? `Sort: ${label}` : "Sort";

/**
 * Renders the search input, sort button, and filters button row.
 * @param root0 - Component props
 * @param root0.searchText - Current search input text
 * @param root0.onSearchChange - Search text change handler
 * @param root0.onClearSearch - Clear search handler
 * @param root0.onInputFocus - Handler when search input gains focus
 * @param root0.onInputBlur - Handler when search input loses focus
 * @param root0.showSortFilter - Whether to show Sort and Filter buttons
 * @param root0.searchPlaceholder - Placeholder text
 * @param root0.onSortPress - Sort button press handler
 * @param root0.onFiltersPress - Filters button press handler
 * @param root0.isDark - Dark mode flag
 * @param root0.activeFilterCount - Number of active filters
 * @param root0.activeSortLabel - Active sort field label
 * @param root0.activeSortDir - Active sort direction
 * @param root0.sortTestID - Optional testID for the Sort button
 * @param root0.filterTestID - Optional testID for the Filters button
 */
const MobileSearchSortFilterBarView = ({
  searchText,
  onSearchChange,
  onClearSearch,
  onInputFocus,
  onInputBlur,
  showSortFilter = true,
  searchPlaceholder,
  onSortPress,
  onFiltersPress,
  activeFilterCount,
  activeSortLabel,
  activeSortDir,
  sortTestID,
  filterTestID,
}: MobileSearchSortFilterBarViewProps) => (
  // SE-7707: the row wraps rather than nominating a casualty. The Input keeps
  // a floor equal to its own intrinsic minimum (208px, the `basis-52` rung —
  // measured: 28px leading-icon slot + 178px `<input>` intrinsic size + 2px
  // border) and the chips are free to move to a second line when they cannot
  // sit beside it. Before this, the row was `[flex-1 Input][shrink-0 chips]`,
  // so the Input's basis was 0 and it absorbed 100% of every shortfall while
  // the chips yielded none — and the Input's wrapper clips rather than
  // ellipsizing, so the shortfall landed mid-glyph on the placeholder.
  // The chip label is user data (a rating name), which is why three earlier
  // fixes verified clean against a short label and shipped: SE-4146 →
  // SE-5574 → SE-5594. Wrapping makes label length decide only WHEN the row
  // goes to two lines, never WHETHER anything is clipped.
  <Stack direction="horizontal" className="flex-wrap items-center gap-2">
    <Input
      variant="outline"
      // SE-5574: the small input size keeps the field text/placeholder on the
      // same body type scale as the Sort/Filters chips, so "Search players"
      // fits without clipping (the default size uses the wider title scale).
      size="sm"
      className="shrink grow basis-52 rounded-sm"
      leadingIcon={Search}
      trailingIcon={searchText.length > 0 ? X : undefined}
      onTrailingIconPress={searchText.length > 0 ? onClearSearch : undefined}
      field={{
        placeholder: searchPlaceholder,
        value: searchText,
        onChangeText: onSearchChange,
        onFocus: onInputFocus,
        onBlur: onInputBlur,
        returnKeyType: "search",
        autoCapitalize: "none",
        autoCorrect: false,
      }}
    />

    {showSortFilter ? (
      // SE-7707: the chips are the side that yields. They keep their labels
      // whenever they fit beside the input and move to a second line when
      // they do not, so the active sort field stays readable at every width
      // (SE-4146) instead of the search placeholder being cut. The pair
      // `min-w-0 shrink` is the tail case only — a line narrower than the
      // chips' own content, which no shipping phone reaches.
      // SE-5594: at narrow widths the Filters chip still compacts to icon +
      // count badge, which keeps the row on one line further down the range.
      <Stack
        direction="horizontal"
        className="min-w-0 shrink items-center gap-2"
      >
        <Pressable
          testID={sortTestID}
          onPress={onSortPress}
          accessibilityRole="button"
          accessibilityLabel="Open sort options"
          // SE-5574: absolute width cap (was a one-third fraction) — a
          // fractional cap is relative to the chips row, which is
          // content-width in the wide single-row layout and would re-truncate
          // the "Sort: X" label (SE-4146).
          className={`max-w-48 ${chipClass(Boolean(activeSortLabel))}`}
        >
          <Icon
            as={getSortIcon(activeSortLabel, activeSortDir)}
            size="sm"
            className={`shrink-0 ${activeSortLabel ? "text-accent-primary" : "text-content-muted"}`}
          />
          <Text
            variant="body"
            className={`shrink line-clamp-1 ${activeSortLabel ? "text-accent-primary" : "text-content-secondary"}`}
          >
            {getSortButtonLabel(activeSortLabel)}
          </Text>
        </Pressable>

        <Pressable
          testID={filterTestID}
          onPress={onFiltersPress}
          accessibilityRole="button"
          accessibilityLabel="Open filter options"
          className={chipClass(activeFilterCount > 0)}
        >
          <Icon
            as={SlidersHorizontal}
            size="sm"
            className={
              activeFilterCount > 0
                ? "text-accent-primary"
                : "text-content-muted"
            }
          />
          <Text
            variant="body"
            // SE-5594: at narrow widths, show only the icon and count badge
            // to preserve space for the input.
            className={`hidden sm:flex ${activeFilterCount > 0 ? "text-accent-primary" : "text-content-secondary"}`}
          >
            Filters
          </Text>
          <ActiveFilterCountBadge count={activeFilterCount} />
        </Pressable>
      </Stack>
    ) : null}
  </Stack>
);

MobileSearchSortFilterBarView.displayName = "MobileSearchSortFilterBarView";

export default memo(MobileSearchSortFilterBarView);
